import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Check, AlertTriangle, Clock, Loader2, CheckSquare, Square,
  Send, CalendarDays, MessageSquare, ArrowRight, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getDeadlineInfo(dueDateStr) {
  if (!dueDateStr) return null;
  const now = new Date();
  const due = new Date(dueDateStr);
  const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, cls: 'text-red-600 bg-red-50 border-red-200' };
  if (diffDays <= 3) return { label: `Due in ${diffDays}d`, cls: 'text-amber-600 bg-amber-50 border-amber-200' };
  return { label: `${diffDays}d left`, cls: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
}

export default function StakeholderPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/stakeholder-portal/${token}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Invalid or expired portal link.' : 'Failed to load portal.');
      } finally { setLoading(false); }
    };
    load();
  }, [token]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.entries;
    return data.entries.filter(e => e.approval_status === filter);
  }, [data, filter]);

  const pendingEntries = useMemo(() => data ? data.entries.filter(e => e.approval_status === 'pending') : [], [data]);

  const toggleSelect = (at) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(at) ? next.delete(at) : next.add(at);
      return next;
    });
  };

  const selectAllPending = () => {
    if (selected.size === pendingEntries.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingEntries.map(e => e.approval_token)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    setProcessing(true);
    setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/stakeholder-portal/${token}/bulk-action`, {
        approval_tokens: Array.from(selected),
        action,
        comment: comment.trim() || null,
      });
      setResult(res.data);
      setSelected(new Set());
      setComment('');
      // Refresh data
      const refresh = await axios.get(`${API_URL}/api/stakeholder-portal/${token}`);
      setData(refresh.data);
    } catch {
      setResult({ error: 'Failed to process action. Please try again.' });
    } finally { setProcessing(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#4ABFCF] animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="py-12 text-center">
          <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="stakeholder-portal">
      {/* Header */}
      <div className="bg-[#0F2040] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base font-bold">Seertech Solutions</span>
                <span className="text-[10px] text-[#4ABFCF] tracking-wider font-semibold">LMS ESTIMATOR</span>
              </div>
              <h1 className="text-xl font-bold">Approval Portal</h1>
              <p className="text-sm text-slate-300 mt-0.5">
                {data.recipient_name} &middot; {data.recipient_email}
              </p>
            </div>
            <Link to={`/preferences/${token}`}
              className="text-xs text-[#4ABFCF] hover:text-white flex items-center gap-1 transition-colors"
              data-testid="portal-preferences-link">
              <Settings className="w-3.5 h-3.5" /> Preferences
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total', value: data.summary.total, color: 'text-[#0F2040]' },
            { label: 'Pending', value: data.summary.pending, color: 'text-slate-500' },
            { label: 'Approved', value: data.summary.approved, color: 'text-emerald-600' },
            { label: 'Changes Req.', value: data.summary.changes_requested, color: 'text-amber-600' },
          ].map(k => (
            <div key={k.label} className="bg-white rounded-lg border border-slate-200 px-4 py-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase font-semibold">{k.label}</p>
              <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
            </div>
          ))}
        </div>

        {/* Result notification */}
        {result && !result.error && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center gap-2" data-testid="bulk-result">
            <Check className="w-5 h-5 text-emerald-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-700">
                {result.processed} item{result.processed !== 1 ? 's' : ''} {result.action === 'approve' ? 'approved' : 'changes requested'}
              </p>
            </div>
          </div>
        )}
        {result && result.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">{result.error}</div>
        )}

        {/* Filter + Actions Bar */}
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              {/* Filter tabs */}
              <div className="flex items-center gap-1">
                {[
                  { id: 'pending', label: `Pending (${data.summary.pending})` },
                  { id: 'approved', label: `Approved (${data.summary.approved})` },
                  { id: 'changes_requested', label: `Changes (${data.summary.changes_requested})` },
                  { id: 'all', label: `All (${data.summary.total})` },
                ].map(f => (
                  <button key={f.id} onClick={() => { setFilter(f.id); setSelected(new Set()); }}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                      filter === f.id ? 'bg-[#0F2040] text-white' : 'text-slate-500 hover:bg-slate-100'
                    }`} data-testid={`filter-${f.id}`}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Bulk actions */}
              {filter === 'pending' && pendingEntries.length > 0 && (
                <div className="flex items-center gap-2">
                  <button onClick={selectAllPending}
                    className="text-xs text-[#4ABFCF] hover:text-[#3AAFBF] font-medium transition-colors"
                    data-testid="select-all-btn">
                    {selected.size === pendingEntries.length ? 'Deselect All' : `Select All (${pendingEntries.length})`}
                  </button>
                  {selected.size > 0 && (
                    <>
                      <span className="text-xs text-slate-400">{selected.size} selected</span>
                      <button onClick={() => handleBulkAction('approve')} disabled={processing}
                        className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                        data-testid="bulk-approve-btn">
                        {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Approve
                      </button>
                      <button onClick={() => handleBulkAction('changes_requested')} disabled={processing}
                        className="text-xs px-3 py-1.5 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center gap-1"
                        data-testid="bulk-changes-btn">
                        {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
                        Request Changes
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Comment field */}
            {selected.size > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <label className="text-[10px] text-slate-400 uppercase font-semibold mb-1 block">
                  Comment (optional — applies to all selected)
                </label>
                <textarea value={comment} onChange={e => setComment(e.target.value)}
                  rows={2} placeholder="Add a note for the estimate owners..."
                  className="w-full border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#4ABFCF] focus:border-[#4ABFCF] resize-y"
                  data-testid="bulk-comment" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Entry List */}
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <Check className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No {filter === 'all' ? '' : filter} reviews found</p>
              </CardContent>
            </Card>
          ) : (
            filtered.map((entry, idx) => {
              const isPending = entry.approval_status === 'pending';
              const isSelected = selected.has(entry.approval_token);
              const deadline = getDeadlineInfo(entry.due_date);
              return (
                <Card key={entry.approval_token || `entry-${idx}`}
                  className={`transition-all ${isSelected ? 'ring-2 ring-[#4ABFCF] border-[#4ABFCF]' : ''} ${
                    entry.approval_status === 'approved' ? 'bg-emerald-50/30' :
                    entry.approval_status === 'changes_requested' ? 'bg-amber-50/30' : ''
                  }`}
                  data-testid={`portal-entry-${entry.approval_token}`}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-start gap-3">
                      {/* Checkbox (pending only) */}
                      {isPending && (
                        <button onClick={() => toggleSelect(entry.approval_token)}
                          className="mt-0.5 shrink-0"
                          data-testid={`select-${entry.approval_token}`}>
                          {isSelected
                            ? <CheckSquare className="w-5 h-5 text-[#4ABFCF]" />
                            : <Square className="w-5 h-5 text-slate-300 hover:text-slate-400 transition-colors" />
                          }
                        </button>
                      )}

                      {/* Status icon (non-pending) */}
                      {!isPending && (
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                          entry.approval_status === 'approved' ? 'bg-emerald-100' : 'bg-amber-100'
                        }`}>
                          {entry.approval_status === 'approved'
                            ? <Check className="w-3.5 h-3.5 text-emerald-600" />
                            : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                          }
                        </div>
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-[#0F2040]">{entry.estimate_name || 'Untitled Estimate'}</p>
                          <Badge className={`text-[8px] ${
                            entry.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                            entry.approval_status === 'changes_requested' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            'bg-slate-100 text-slate-500 border-slate-200'
                          }`}>
                            {entry.approval_status === 'approved' ? 'Approved' :
                             entry.approval_status === 'changes_requested' ? 'Changes Requested' : 'Pending'}
                          </Badge>
                          {deadline && isPending && (
                            <Badge className={`text-[8px] border ${deadline.cls}`} data-testid={`portal-deadline-${entry.approval_token}`}>
                              <CalendarDays className="w-2.5 h-2.5 mr-0.5" /> {deadline.label}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
                          <span className="flex items-center gap-0.5">
                            <Send className="w-2.5 h-2.5" /> From {entry.sender_name}
                          </span>
                          {entry.sent_at && (
                            <span>{new Date(entry.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          )}
                        </div>
                        {entry.approval_comment && (
                          <div className="mt-2 bg-white rounded px-2.5 py-1.5 border border-slate-200/80">
                            <p className="text-[9px] font-semibold text-slate-500 flex items-center gap-1 mb-0.5">
                              <MessageSquare className="w-2.5 h-2.5" /> Your Comment
                            </p>
                            <p className="text-xs text-slate-600">{entry.approval_comment}</p>
                          </div>
                        )}
                      </div>

                      {/* Quick action for pending single item */}
                      {isPending && !isSelected && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Link to={`/approval/${entry.approval_token}`}
                            className="text-[10px] text-[#4ABFCF] hover:text-[#3AAFBF] flex items-center gap-0.5 font-medium transition-colors"
                            data-testid={`review-link-${entry.approval_token}`}>
                            Review <ArrowRight className="w-3 h-3" />
                          </Link>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-[10px] text-slate-400">Approval Portal by Seertech Solutions &middot; Seertech Blueprint Studio</p>
        </div>
      </div>
    </div>
  );
}
